"use client";

import { MessageCircle } from "lucide-react";
import { useRouter } from "next/navigation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { FadeInUp } from "@/components/ui/motion";
import { FinalCTA } from "../components/FinalCTA";

const faqCategories = [
  {
    title: "Getting Started",
    questions: [
      {
        q: "How do I create my first workflow?",
        a: "After signing up, you'll be guided through creating your first workflow. Choose a trigger (like a webhook or schedule), add actions, and deploy-all from the visual builder.",
      },
      {
        q: "Do I need coding experience?",
        a: "No coding required! Our visual builder lets you create powerful automations by connecting blocks. However, developers can add custom code blocks for advanced logic.",
      },
      {
        q: "Can I import workflows from other tools?",
        a: "Yes, we support importing from n8n, Zapier, and Make. Contact support for migration assistance.",
      },
    ],
  },
  {
    title: "Billing & Plans",
    questions: [
      {
        q: "What counts as a workflow run?",
        a: "A workflow run is counted each time your workflow executes from trigger to completion. Retries within a run don't count as additional runs.",
      },
      {
        q: "Can I change plans anytime?",
        a: "Yes, upgrade or downgrade anytime. Upgrades take effect immediately. Downgrades apply at your next billing cycle.",
      },
      {
        q: "Is there a free trial?",
        a: "We offer a generous free tier with 1,000 runs/month-no credit card required. Use it as long as you need.",
      },
    ],
  },
  {
    title: "Technical",
    questions: [
      {
        q: "What's the maximum workflow complexity?",
        a: "Workflows can have up to 100 nodes on all plans. Enterprise customers can request higher limits.",
      },
      {
        q: "How do you handle failures?",
        a: "Automatic retries with exponential backoff, dead-letter queues for failed runs, and full execution replay for debugging.",
      },
      {
        q: "Can I self-host Makeplans?",
        a: "Enterprise plans include self-hosted deployment options. Contact sales for details.",
      },
    ],
  },
  {
    title: "Security & Compliance",
    questions: [
      {
        q: "Where is my data stored?",
        a: "Data is stored in SOC 2 Type II compliant data centers. US and EU regions available.",
      },
      {
        q: "Do you support SSO?",
        a: "Yes, Team and Enterprise plans support SAML SSO with any identity provider.",
      },
      {
        q: "How are API keys protected?",
        a: "All credentials are encrypted at rest using AES-256 and stored in isolated vaults. Keys are never exposed in logs or UI.",
      },
    ],
  },
];

const FAQPage = () => {
  const navigate = useRouter();

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        {/* Hero */}
        <section className="py-16 lg:py-24 relative overflow-hidden">
          {/* Enhanced background decoration */}
          <div className="absolute top-10 right-1/4 w-[600px] h-[600px] bg-primary/12 rounded-full blur-[140px] pointer-events-none" />
          <div className="absolute bottom-0 left-1/4 w-[450px] h-[450px] bg-accent/15 rounded-full blur-[120px] pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <FadeInUp className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
                Frequently asked{" "}
                <span className="gradient-text">questions</span>
              </h1>
              <p className="text-lg text-muted-foreground">
                Find answers to common questions. Can't find what you're looking
                for? Contact our team.
              </p>
            </FadeInUp>
          </div>
        </section>

        {/* FAQ Categories */}
        <section className="pb-20 pt-4 section-feature relative">
          <div className="absolute inset-0 pattern-grid opacity-15 pointer-events-none" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl relative">
            {faqCategories.map((category, categoryIndex) => (
              <FadeInUp
                key={category.title}
                delay={categoryIndex * 0.1}
                className="mb-12"
              >
                <h2 className="text-xl font-semibold text-foreground mb-6 pb-2 border-b border-border">
                  {category.title}
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {category.questions.map((item, index) => (
                    <AccordionItem
                      key={index}
                      value={`${category.title}-${index}`}
                      className="bg-card border border-border rounded-xl px-6 data-[state=open]:border-primary/50"
                    >
                      <AccordionTrigger className="text-left hover:no-underline py-5">
                        <span className="font-medium text-foreground">
                          {item.q}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pb-5">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </FadeInUp>
            ))}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 section-gradient border-t border-border/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <FadeInUp>
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Still have questions?
              </h2>
              <p className="text-muted-foreground mb-6">
                Our team is here to help. Reach out anytime.
              </p>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate.push("/contact")}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Contact support
              </Button>
            </FadeInUp>
          </div>
        </section>

        <FinalCTA />
      </main>
    </div>
  );
};

export default FAQPage;
